import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';
import { documentService, type Document, type DocumentCompletionStatus } from '../services/document.service';
import { docusignService } from '../services/docusign.service';

export default function OnboardingStep3() {
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [error, setError] = useState('');
    const [documentStatus, setDocumentStatus] = useState<DocumentCompletionStatus | null>(null);
    const [activeDocument, setActiveDocument] = useState<Document | null>(null);
    const [signingDocument, setSigningDocument] = useState<string | null>(null);
    const [signingAllDocs, setSigningAllDocs] = useState(false);
    const [signingInProgress, setSigningInProgress] = useState(false);
    const [signatureData, setSignatureData] = useState({
        fullName: '',
        agreedToTerms: false
    });
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Load documents on mount
    useEffect(() => {
        loadDocuments();
    }, []);

    // Auto-fill signature data with user's name
    useEffect(() => {
        if (user && user.firstName && user.lastName && !signatureData.fullName) {
            setSignatureData(prev => ({
                ...prev,
                fullName: `${user.firstName} ${user.lastName}`
            }));
        }
    }, [user]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Auto-start polling if there are documents in progress
    useEffect(() => {
        if (documentStatus) {
            const inProgressDocs = documentStatus.documents.filter(
                d => d.status === 'IN_PROGRESS' || d.status === 'VIEWED'
            );

            // If there are in-progress documents, start polling
            if (inProgressDocs.length > 0 && !pollingIntervalRef.current) {
                console.log('Found in-progress documents, starting automatic polling...');
                // Get the envelope ID from the first in-progress document
                const envelopeId = inProgressDocs[0].docusignEnvelopeId;
                startPollingForCompletion(envelopeId);
            }
        }
    }, [documentStatus]);

    // Start polling for document completion
    const startPollingForCompletion = (envelopeId?: string) => {
        setSigningInProgress(true);

        // Clear any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        // Poll every 3 seconds for document completion
        pollingIntervalRef.current = setInterval(async () => {
            try {
                // If we have an envelope ID, check DocuSign status directly
                if (envelopeId) {
                    try {
                        const statusResponse = await fetch(`/api/docusign/envelope/${envelopeId}/status`, {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            },
                        });

                        if (statusResponse.ok) {
                            const statusData = await statusResponse.json();
                            console.log('Envelope status:', statusData);

                            // If completed, refresh documents
                            if (statusData.data?.completed) {
                                console.log('Envelope completed! Refreshing documents...');
                            }
                        }
                    } catch (err) {
                        console.error('Error checking envelope status:', err);
                        // Continue with document refresh even if envelope check fails
                    }
                }

                // Always refresh document list to get updated status (more reliable than webhooks in dev)
                const response = await documentService.getMyDocuments();
                if (response.success) {
                    setDocumentStatus(response.data);

                    // Check if all required documents are signed
                    const requiredDocs = response.data.documents.filter(d => d.isRequired);
                    const allSigned = requiredDocs.every(d => d.status === 'SIGNED' || d.status === 'COMPLETED');

                    if (allSigned) {
                        // Stop polling
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        setSigningInProgress(false);
                    }
                }
            } catch (err) {
                console.error('Error polling document status:', err);
            }
        }, 3000);
    };

    const loadDocuments = async () => {
        try {
            setDocumentsLoading(true);
            const response = await documentService.getMyDocuments();
            if (response.success) {
                setDocumentStatus(response.data);
            }
        } catch (err: any) {
            console.error('Error loading documents:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load documents');
        } finally {
            setDocumentsLoading(false);
        }
    };

    const handleViewDocument = (doc: Document) => {
        setActiveDocument(doc);
        setError('');
    };

    const closeModal = () => {
        setActiveDocument(null);
    };

    // Sign all unsigned documents with DocuSign in one session
    const handleSignAllDocuments = async () => {
        if (!signatureData.fullName.trim()) {
            setError('Please enter your full name to sign');
            return;
        }

        // Get all unsigned documents that use DocuSign
        const unsignedDocs = documentStatus?.documents.filter(
            doc => doc.status !== 'SIGNED' && doc.status !== 'COMPLETED' && doc.useDocuSign
        ) || [];

        if (unsignedDocs.length === 0) {
            setError('No documents to sign');
            return;
        }

        setSigningAllDocs(true);
        setError('');

        // Open blank window immediately to avoid popup blocker
        const newWindow = window.open('about:blank', '_blank');

        try {
            const documentIds = unsignedDocs.map(doc => doc.id);
            console.log('Creating DocuSign session for all documents:', documentIds);

            const response = await docusignService.createSigningSession(documentIds);

            if (response.success && response.data?.signingUrl) {
                if (newWindow) {
                    newWindow.location.href = response.data.signingUrl;
                } else {
                    window.location.href = response.data.signingUrl;
                    return;
                }
                // Start polling for completion
                startPollingForCompletion();
            } else if (response.setupRequired) {
                if (newWindow) newWindow.close();
                setError('DocuSign is not configured. Please contact support.');
            } else {
                if (newWindow) newWindow.close();
                setError(response.error || 'Failed to create signing session');
            }
        } catch (err: any) {
            if (newWindow) newWindow.close();
            console.error('Error signing documents:', err);
            setError(err.response?.data?.error || err.message || 'Failed to sign documents');
        } finally {
            setSigningAllDocs(false);
        }
    };

    const handleSignDocument = async (doc: Document) => {
        if (!signatureData.fullName.trim()) {
            setError('Please enter your full name to sign');
            return;
        }

        setSigningDocument(doc.id);
        setError('');

        try {
            if (doc.useDocuSign) {
                // Use DocuSign for this document
                console.log('Creating DocuSign session for document:', doc.id);

                // Open blank window immediately (before async call) to avoid popup blocker
                const newWindow = window.open('about:blank', '_blank');

                try {
                    const response = await docusignService.createSigningSession([doc.id]);

                    if (response.success && response.data?.signingUrl) {
                        // Redirect the already-opened window to DocuSign
                        if (newWindow) {
                            newWindow.location.href = response.data.signingUrl;
                        } else {
                            // Fallback: redirect current page if popup was blocked
                            window.location.href = response.data.signingUrl;
                            return;
                        }
                        // Close the modal and show a message
                        closeModal();
                        setError('');
                        // Start polling for completion with envelope ID
                        startPollingForCompletion(response.data.envelopeId);
                        return;
                    } else if (response.setupRequired) {
                        // Close the blank window if DocuSign not configured
                        if (newWindow) newWindow.close();
                        // Don't fall back to manual signing - show error instead
                        setError('DocuSign is not configured. Please contact support to enable document signing.');
                    } else {
                        // Close the blank window on error
                        if (newWindow) newWindow.close();
                        setError(response.error || 'Failed to create signing session');
                    }
                } catch (err: any) {
                    // Close the blank window on error
                    if (newWindow) newWindow.close();
                    throw err;
                }
            } else {
                // Manual signing (only for documents that don't use DocuSign)
                await signDocumentManually(doc);
            }
        } catch (err: any) {
            console.error('Error signing document:', err);
            // Don't fall back to manual signing for DocuSign documents
            setError(err.response?.data?.error || err.message || 'Failed to sign document');
        } finally {
            setSigningDocument(null);
        }
    };

    const signDocumentManually = async (doc: Document) => {
        try {
            const response = await documentService.signDocument(doc.id, {
                fullName: signatureData.fullName,
                timestamp: new Date().toISOString()
            });

            if (response.success) {
                // Update local state
                setDocumentStatus(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        completedDocuments: prev.completedDocuments + 1,
                        pendingDocuments: prev.pendingDocuments - 1,
                        completionPercentage: Math.round(((prev.completedDocuments + 1) / prev.totalDocuments) * 100),
                        documents: prev.documents.map(d =>
                            d.id === doc.id
                                ? { ...d, status: 'SIGNED', signedAt: new Date().toISOString() }
                                : d
                        )
                    };
                });
                closeModal();
            } else {
                setError('Failed to sign document');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to sign document');
        }
    };

    // Development helper function to mark documents as completed
    const markAsCompleted = async (documentId: string) => {
        if (process.env.NODE_ENV !== 'development') return;
        
        try {
            const response = await documentService.signDocument(documentId, {
                fullName: signatureData.fullName || 'Development User',
                timestamp: new Date().toISOString()
            });

            if (response.success) {
                // Update local state
                setDocumentStatus(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        completedDocuments: prev.completedDocuments + 1,
                        pendingDocuments: prev.pendingDocuments - 1,
                        completionPercentage: Math.round(((prev.completedDocuments + 1) / prev.totalDocuments) * 100),
                        documents: prev.documents.map(d =>
                            d.id === documentId
                                ? { ...d, status: 'SIGNED', signedAt: new Date().toISOString() }
                                : d
                        )
                    };
                });
            }
        } catch (err: any) {
            console.error('Failed to mark document as completed:', err);
        }
    };

    const handleContinue = async () => {
        await refreshUser();
        navigate('/onboarding/step-4');
    };

    const getDocumentIcon = (status: string) => {
        if (status === 'SIGNED' || status === 'COMPLETED') {
            return (
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
            );
        }
        if (status === 'VIEWED' || status === 'IN_PROGRESS') {
            return (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </div>
            );
        }
        return (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            </div>
        );
    };

    const getStatusBadge = (status: string) => {
        if (status === 'SIGNED' || status === 'COMPLETED') {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">Signed</span>;
        }
        if (status === 'VIEWED' || status === 'IN_PROGRESS') {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Viewed</span>;
        }
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Pending</span>;
    };

    const requiredDocs = documentStatus?.documents.filter(d => d.isRequired) || [];
    const allRequiredSigned = requiredDocs.every(d => d.status === 'SIGNED' || d.status === 'COMPLETED');
    const signedCount = requiredDocs.filter(d => d.status === 'SIGNED' || d.status === 'COMPLETED').length;

    // In development and production, allow continuing to bypass document signing issues per user request

    if (documentsLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                <LoadingSpinner size="lg" text="Loading documents..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Step 3 of 8</span>
                        <span className="text-sm font-medium text-emerald-600">Sign Documents</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(signedCount / requiredDocs.length) * 100 || 0}%` }}
                        ></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 text-center">
                        {signedCount} of {requiredDocs.length} required documents signed
                    </p>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Sign Your Documents
                        </h1>
                        <p className="text-gray-600">
                            Please review and sign each document below to continue with your onboarding.
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Signing In Progress Banner */}
                    {signingInProgress && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <div>
                                    <p className="text-blue-800 font-medium">DocuSign is open in another tab</p>
                                    <p className="text-blue-600 text-sm">Complete signing there, then return here to continue.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Signature Name Input */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Your Full Legal Name (for signatures)
                        </label>
                        <input
                            type="text"
                            value={signatureData.fullName}
                            onChange={(e) => setSignatureData({ ...signatureData, fullName: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Enter your full legal name"
                        />
                    </div>


                    {/* Document List */}
                    <div className="space-y-4 mb-8">
                        {documentStatus?.documents.map((doc) => (
                            <div
                                key={doc.id}
                                className={`border rounded-lg p-4 transition-all ${doc.status === 'SIGNED' || doc.status === 'COMPLETED'
                                    ? 'border-emerald-200 bg-emerald-50'
                                    : 'border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                                    }`}
                            >
                                <div className="flex items-center gap-4">
                                    {getDocumentIcon(doc.status)}

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-medium text-gray-900 truncate">
                                                {doc.name}
                                            </h3>
                                            {doc.isRequired && (
                                                <span className="text-xs text-red-500 font-medium">Required</span>
                                            )}
                                            {getStatusBadge(doc.status)}
                                        </div>
                                        <p className="text-sm text-gray-600 line-clamp-2">
                                            {doc.description}
                                        </p>
                                        {doc.signedAt && (
                                            <p className="text-xs text-emerald-600 mt-1">
                                                Signed on {new Date(doc.signedAt).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex-shrink-0">
                                        {doc.status === 'SIGNED' || doc.status === 'COMPLETED' ? (
                                            <button
                                                onClick={() => handleViewDocument(doc)}
                                                className="px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
                                            >
                                                View
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleViewDocument(doc)}
                                                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                                            >
                                                Review & Sign
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Continue Button */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                        <button
                            onClick={() => navigate('/onboarding/step-2')}
                            className="text-gray-600 hover:text-gray-800 font-medium"
                        >
                            ← Back
                        </button>

                        <div className="flex flex-col items-end gap-2 text-right">
                            {!allRequiredSigned && (
                                <p className="text-sm text-amber-600 mb-1">
                                    Continue to Training (signing can be completed later)
                                </p>
                            )}
                            <button
                                onClick={handleContinue}
                                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-md hover:shadow-lg active:scale-95"
                            >
                                Continue to Training
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Help Text */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                        Need help? Contact your recruiter or{' '}
                        <a href="mailto:support@turtleconnector.com" className="text-emerald-600 hover:text-emerald-700">
                            support@turtleconnector.com
                        </a>
                    </p>
                </div>
            </div>

            {/* Document Signing Modal */}
            {activeDocument && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={closeModal}
                        ></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        {/* Modal panel */}
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
                            {/* Modal header */}
                            <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {activeDocument.name}
                                    </h3>
                                    <p className="text-sm text-gray-500">{activeDocument.description}</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Modal body - PDF viewer */}
                            <div className="bg-gray-100" style={{ height: '60vh' }}>
                                {activeDocument.documentUrl ? (
                                    <div className="w-full h-full relative">
                                        <iframe
                                            src={activeDocument.documentUrl}
                                            className="w-full h-full border-0"
                                            title={activeDocument.name}
                                            onLoad={() => console.log('PDF loaded successfully')}
                                            onError={(e) => {
                                                console.error('PDF failed to load:', e);
                                                // Hide the iframe and show fallback
                                                e.currentTarget.style.display = 'none';
                                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                if (fallback) fallback.style.display = 'flex';
                                            }}
                                        />
                                        {/* Fallback content - hidden by default */}
                                        <div className="absolute inset-0 flex-col items-center justify-center bg-gray-100" style={{ display: 'none' }}>
                                            <div className="text-center p-8">
                                                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="mt-4 text-gray-600 font-medium">PDF preview not available</p>
                                                <p className="text-sm text-gray-500 mt-1 mb-4">
                                                    Your browser cannot display this PDF inline.
                                                </p>
                                                <a
                                                    href={activeDocument.documentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                    Open PDF in New Tab
                                                </a>
                                            </div>
                                        </div>
                                        {/* Debug info - remove in production */}
                                        {process.env.NODE_ENV === 'development' && (
                                            <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs p-1 rounded max-w-xs truncate">
                                                URL: {activeDocument.documentUrl}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center p-8">
                                            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <p className="mt-4 text-gray-600 font-medium">Document preview not available</p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                The document content will be displayed here once configured.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal footer - Signature area */}
                            <div className="bg-white px-6 py-4 border-t border-gray-200">
                                {activeDocument.status === 'SIGNED' || activeDocument.status === 'COMPLETED' ? (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-emerald-600">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="font-medium">This document has been signed</span>
                                        </div>
                                        <button
                                            onClick={closeModal}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            Close
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Signature confirmation */}
                                        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <p className="text-sm text-amber-800">
                                                By clicking "Sign Document", you acknowledge that you have read and agree to the terms of this document.
                                                Your signature will be: <strong>{signatureData.fullName || '(Enter your name above)'}</strong>
                                            </p>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={closeModal}
                                                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => handleSignDocument(activeDocument)}
                                                disabled={signingDocument === activeDocument.id || !signatureData.fullName.trim()}
                                                className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                {signingDocument === activeDocument.id ? (
                                                    <>
                                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Signing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                        {activeDocument.useDocuSign ? 'Sign with DocuSign' : 'Sign Document'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
